import { AlertTriangle } from 'lucide-react'

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  has_timetable: boolean | null
  timetable_entry_count: number | null
}

interface ConfirmDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  selectedUsers: User[]
  isDeleting: boolean
}

export function ConfirmDeleteModal({ isOpen, onClose, onConfirm, selectedUsers, isDeleting }: ConfirmDeleteModalProps) {
  if (!isOpen) return null

  // Aggregate statistics
  const teachersWithTimetable = selectedUsers.filter(u => u.has_timetable && u.timetable_entry_count && u.timetable_entry_count > 0)
  const totalEntries = teachersWithTimetable.reduce((sum, u) => sum + (u.timetable_entry_count || 0), 0)
  const hasTimetables = totalEntries > 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-bold mb-2">Lehrer löschen</h2>
        
        <p className="text-sm text-gray-600 mb-4">
          Sie möchten <strong>{selectedUsers.length} Benutzer</strong> löschen.
        </p>

        {hasTimetables && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-red-800">
                  ⚠️ {totalEntries} Stundenplan-Einträge werden entfernt
                </div>
                <div className="text-sm text-red-600 mt-1">
                  {teachersWithTimetable.length} der ausgewählten Lehrer haben zusammen {totalEntries} Stundenplan-Einträge.
                  Diese werden beim Löschen unwiderruflich entfernt.
                </div>
                <div className="mt-2 text-xs text-red-500">
                  Der Stundenplan kann nach dem Wiederherstellen des Lehrers nur durch erneuten Import wiederhergestellt werden.
                </div>
              </div>
            </div>
          </div>
        )}

        {!hasTimetables && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="text-sm text-blue-700">
              Keine der ausgewählten Lehrer hat Stundenplan-Einträge.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-40"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-40"
          >
            {isDeleting ? 'Löschen...' : `${selectedUsers.length} Lehrer löschen`}
          </button>
        </div>
      </div>
    </div>
  )
}